/**
 * The audit's Data tab — exactly what this audit covers.
 *
 * The data scope (rows in scope, when they were locked, the conditions and
 * chosen files in words, the split by classification), then the source files
 * that contributed rows. One control: Change data scope. Uploading, mapping
 * and processing are intake's work and do not appear here.
 */

import { useModule } from '../modules/useModule';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { Card, StatusBadge } from '../../shared/ui';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '../../provider/toast';
import {
	useAuditFilesQuery,
	useChangeDataFilterMutation,
} from '../../queries/audits';
import { useAuditCoverageQuery } from '../../queries/reports';
import { useAnalysisOverviewQuery } from '../../queries/analysis';
import { PageHeader } from '../../provider/pageTitle';
import { count, dateTime } from '../../utils/format';
import { DataTable, type Column } from '../shared/tables';
import { ErrorState } from '../shared/states';
import { DataScopeSummary } from '../shared/dataFilter';
import { ClassificationSplit } from '../shared/badges';
import { useAuditContext } from '../audits/AuditWorkspace';
import { ChangeDataScopeDialog } from './ChangeDataScopeDialog';
import { downloadFile } from './FileTable';
import type { CanonicalRowFilter, SelectedFile } from '../../schemas';

const LOCKED_REASON =
	'The report has been issued, so the data scope is locked.';

function DownloadOriginal({ file }: { file: SelectedFile }) {
	const toast = useToast();
	const download = useMutation({
		mutationFn: downloadFile,
		onError: (error) =>
			toast(
				'danger',
				error instanceof Error ? error.message : 'The download failed.',
			),
	});
	return (
		<Button
			loading={download.isPending}
			size="small"
			variant="text"
			disabled={download.isPending}
			onClick={(event) => {
				event.stopPropagation();
				download.mutate(file.pkFileId);
			}}
		>
			Download original file
		</Button>
	);
}

export default function FilesWorkspace() {
	const { audit, refresh, crumbs } = useAuditContext();
	const { link } = useModule();
	const navigate = useNavigate();
	const toast = useToast();
	const [changing, setChanging] = useState(false);

	const files = useAuditFilesQuery(audit.pkAuditId);
	const coverage = useAuditCoverageQuery(audit.pkAuditId);
	const analyses = useAnalysisOverviewQuery(audit.pkAuditId);
	const change = useChangeDataFilterMutation(audit.pkAuditId);
	const locked = audit.status === 'CLOSED';

	/* No classification on an H2R audit: no unclassified count, no split. */
	const isH2R = audit.module === 'H2R';
	const allColumns: Array<Column<SelectedFile>> = [
		{
			key: 'name',
			label: 'File',
			width: 320,
			sortValue: (file) => file.originalName,
			render: (file) => (
				<Link
					to={link(
						`/audits/${audit.pkAuditId}/data/${file.pkFileId}`,
					)}
					className="link"
					onClick={(event) => event.stopPropagation()}
				>
					{file.originalName}
					{file.deletedAt && <span className="mut"> (deleted)</span>}
				</Link>
			),
		},
		{
			key: 'period',
			label: 'Period',
			width: 120,
			sortValue: (file) => file.periodLabel ?? '',
			render: (file) =>
				file.periodLabel ?? <span className="mut">—</span>,
		},
		{
			key: 'rows',
			label: 'Rows in scope',
			num: true,
			width: 130,
			sortValue: (file) => file.coverage.rows,
			render: (file) => count(file.coverage.rows),
		},
		{
			key: 'unclassified',
			label: 'Unclassified',
			num: true,
			width: 120,
			sortValue: (file) => file.coverage.unclassified,
			/* Unclassified rows are left out of every total — a non-zero count is
         the one to notice. */
			render: (file) =>
				file.coverage.unclassified > 0 ? (
					<span className="text-warn b">
						{count(file.coverage.unclassified)}
					</span>
				) : (
					'0'
				),
		},
		{
			key: 'status',
			label: 'Status',
			width: 150,
			sortValue: (file) => file.status,
			render: (file) => (
				<StatusBadge group="fileStatus" value={file.status} />
			),
		},
		{
			key: 'download',
			label: '',
			width: 190,
			render: (file) => <DownloadOriginal file={file} />,
		},
	];
	const columns = allColumns.filter(
		(column) => !(isH2R && column.key === 'unclassified'),
	);

	const save = (filter: CanonicalRowFilter) =>
		change.mutate(filter, {
			onSuccess: () => {
				setChanging(false);
				void refresh();
				toast('ok', 'Data scope saved.');
			},
		});

	return (
		<div className="stack">
			<PageHeader
				breadcrumbs={crumbs}
				title="Data"
				actions={
					<Button
						variant="outlined"
						disabled={locked}
						title={locked ? LOCKED_REASON : undefined}
						onClick={() => setChanging(true)}
					>
						Change data scope
					</Button>
				}
			/>

			<Card
				title="Data scope"
				actions={
					<span className="mut">
						{count(audit.rowCount)} {isH2R ? 'employees' : 'rows'}{' '}
						in scope
						{audit.scopeLockedAt && (
							<> · locked {dateTime(audit.scopeLockedAt)}</>
						)}
					</span>
				}
			>
				<div className="stack">
					<DataScopeSummary scope={audit.dataScope} />
					{!isH2R && coverage.data && coverage.data.rows > 0 && (
						<ClassificationSplit
							rows={{
								SALES: coverage.data.sales,
								RETURNS: coverage.data.returns,
								ADJUSTMENT: coverage.data.adjustments,
								UNKNOWN: coverage.data.unknown,
								EXCLUDED: 0,
							}}
							total={coverage.data.rows}
							compact
						/>
					)}
				</div>
			</Card>

			<ErrorState error={files.error} onRetry={files.refetch} />

			<Card title="Source files">
				<div className="card-bd flush">
					<DataTable
						rows={files.data ?? []}
						columns={columns}
						rowKey={(file) => file.pkFileId}
						onRowClick={(file) =>
							navigate(
								link(
									`/audits/${audit.pkAuditId}/data/${file.pkFileId}`,
								),
							)
						}
						caption="Source files"
						loading={files.isPending}
						empty="No files contribute rows to this audit."
					/>
				</div>
			</Card>

			<ChangeDataScopeDialog
				open={changing}
				clientId={audit.fkClientId}
				clientName={audit.clientName}
				initial={audit.dataFilter}
				hasResults={(analyses.data?.summary.completed ?? 0) > 0}
				pending={change.isPending}
				error={change.error}
				onCancel={() => setChanging(false)}
				onConfirm={save}
			/>
		</div>
	);
}
